import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container, Box, Alert, Snackbar, useMediaQuery, useTheme } from '@mui/material';
import MessageListPanel from './components/MessageListPanel';
import MessageDetailPanel from './components/MessageDetailPanel';
import { getConversationByCard } from './api/messagesApi';

export default function MessagesPage() {
  const { conversationId } = useParams<{ conversationId?: string }>();
  const [searchParams] = useSearchParams();
  const cardId = searchParams.get('cardId');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(
    conversationId || null
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // 페이지 진입 시 스크롤 최상단으로 이동
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // cardId로 진입 시 서버에서 대화 조회/생성 후 바로 대화창 표시
  useEffect(() => {
    if (cardId) {
      getConversationByCard(Number(cardId))
        .then(detail => {
          setSelectedConversationId(String(detail.id));
        })
        .catch((err: unknown) => {
          const message = err instanceof Error && err.message
            ? err.message
            : '대화를 불러오지 못했습니다. 다시 시도해주세요.';
          setErrorMessage(message);
        });
    }
  }, [cardId]);

  // 모바일: selectedConversationId 있으면 대화상세만, 없으면 목록만
  if (isMobile) {
    if (selectedConversationId) {
      return (
        <Box sx={{ height: '100dvh', bgcolor: '#F5F7FA', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <MessageDetailPanel
            conversationId={selectedConversationId}
            onBack={() => setSelectedConversationId(null)}
          />
        </Box>
      );
    }
    return (
      <Box sx={{ minHeight: '100vh', bgcolor: '#F5F7FA', py: 2 }}>
        <Container maxWidth="lg">
          <MessageListPanel
            selectedId={selectedConversationId}
            onSelect={setSelectedConversationId}
          />
        </Container>
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={4000}
          onClose={() => setErrorMessage(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ fontSize: '0.95rem' }}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    );
  }

  // 데스크톱: 좌측 목록 + 우측 대화 (화면 전체 활용)
  return (
    <Box sx={{ height: '100vh', width: '100%', bgcolor: '#F5F7FA', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', px: { md: 2, lg: 3 }, py: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden', gap: 2 }}>
          {/* 좌측: 대화 목록 — 30%, 최소 280px, 최대 400px */}
          <Box sx={{ width: '30%', minWidth: 280, maxWidth: 400, flexShrink: 0, overflow: 'hidden', display: 'flex' }}>
            <MessageListPanel
              selectedId={selectedConversationId}
              onSelect={setSelectedConversationId}
            />
          </Box>

          {/* 우측: 대화 상세 — 나머지 전부 */}
          <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex' }}>
            <MessageDetailPanel conversationId={selectedConversationId} />
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ fontSize: '0.95rem' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
